import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, MessageSquare, CheckCircle, ChevronLeft, RefreshCw } from 'lucide-react';

export default function UserDashboard() {
  const navigate = useNavigate();
  const [scheduledCalls, setScheduledCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const userName = localStorage.getItem('user_name') || 'User';
  const userEmail = localStorage.getItem('user_email');
  const isAuthenticated = localStorage.getItem('user_authenticated') === 'true' || localStorage.getItem('admin_authenticated') === 'true';

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${apiBase}/api/user/scheduled-calls?email=${encodeURIComponent(userEmail)}`);
      if (response.ok) {
        const data = await response.json();
        setScheduledCalls(data);
      } else {
        setError('Failed to fetch your scheduled calls.');
      }
    } catch (err) {
      setError('Network error while fetching dashboard data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchDashboardData();
  }, [isAuthenticated, navigate, userEmail]);

  const isTimeReached = (date, time) => {
    try {
      const callDateTime = new Date(`${date}T${time}`);
      return new Date() >= callDateTime;
    } catch(e) { return true; }
  };

  const handleJoinCall = async (call) => {
    try {
      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${apiBase}/api/meet/jwt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          user_name: userName || "Seeker", 
          user_email: userEmail || "",
          room_name: `scheduled-${call.id}`
        })
      });
      const data = await response.json();
      if (response.ok && data.url) {
        window.open(data.url, '_blank');
      } else {
        console.error('Failed to generate meet url:', data);
      }
    } catch (error) {
      console.error('Error joining call:', error);
    }
  };

  const handleCancelCall = async (callId) => {
    if (!window.confirm("Are you sure you want to cancel this scheduled call?")) return;
    try {
      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${apiBase}/api/user/scheduled-calls/${callId}/cancel`, {
        method: 'PUT'
      });
      if (response.ok) {
        setScheduledCalls(prev => prev.filter(c => c.id !== callId));
      } else {
        setError('Failed to cancel call');
      }
    } catch (error) {
      console.error('Error cancelling call:', error);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(145deg, #ffffff 0%, #FAF6F0 100%)', color: 'var(--primary-gold)' }}>
        <div className="font-serif" style={{ fontSize: '1.5rem', animation: 'pulse 2s infinite' }}>Loading your dashboard...</div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-deep)', paddingTop: '120px', paddingBottom: '60px' }}>
      <div className="container" style={{ maxWidth: '1000px', margin: '0 auto', padding: '0 20px' }}>
        
        {/* Header Section */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '40px', borderBottom: '1px solid rgba(214,179,106,0.2)', paddingBottom: '20px' }}>
          <div>
            <button 
              onClick={() => navigate(-1)} 
              style={{ background: 'none', border: 'none', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', padding: 0, marginBottom: '15px', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}
            >
              <ChevronLeft size={16} /> Back
            </button>
            <h1 className="font-serif" style={{ fontSize: 'clamp(1.8rem, 5vw, 2.5rem)', color: 'var(--primary-gold)', margin: '0 0 10px 0' }}>Welcome, {userName}</h1>
            <p style={{ color: '#4a4a4a', margin: 0, fontSize: '1.1rem' }}>Your personal dashboard and scheduled calls.</p>
          </div>
        </div>

        {/* Content Section */}
        <div className="glass-card animate-fade-in" style={{ padding: 'clamp(20px, 5vw, 40px)', borderRadius: '16px', background: 'rgba(250, 247, 242, 0.8)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', border: '1px solid rgba(181, 137, 37, 0.2)', boxShadow: '0 20px 50px rgba(41, 33, 26, 0.08)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', flexWrap: 'wrap', gap: '15px' }}>
            <h2 className="font-serif" style={{ fontSize: '1.8rem', color: '#2b2520', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Calendar size={24} color="var(--primary-gold)" />
              Your Scheduled Expert Calls
            </h2>
            <button
              onClick={fetchDashboardData}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: 'rgba(214,179,106,0.1)',
                border: '1px solid rgba(214,179,106,0.3)',
                color: 'var(--primary-gold)',
                padding: '8px 16px',
                borderRadius: '20px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '600',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => e.currentTarget.style.background = 'rgba(214,179,106,0.2)'}
              onMouseOut={(e) => e.currentTarget.style.background = 'rgba(214,179,106,0.1)'}
            >
              <RefreshCw size={16} /> Refresh
            </button>
          </div>

          {error && (
            <div style={{ padding: '15px', background: 'rgba(231, 76, 60, 0.1)', color: '#e74c3c', borderRadius: '8px', marginBottom: '20px', border: '1px solid rgba(231, 76, 60, 0.2)' }}>
              {error}
            </div>
          )}

          {scheduledCalls.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', backgroundColor: 'rgba(255,255,255,0.4)', borderRadius: '12px', border: '1px dashed rgba(214,179,106,0.5)', boxShadow: 'inset 0 0 20px rgba(214,179,106,0.05)' }}>
              <div className="animate-float" style={{ display: 'inline-flex', justifyContent: 'center', alignItems: 'center', width: '80px', height: '80px', borderRadius: '50%', background: 'var(--primary-gold-trans)', border: '1px solid var(--primary-gold-border)', marginBottom: '20px', boxShadow: '0 0 20px rgba(214,179,106,0.2)' }}>
                <Calendar size={36} color="var(--primary-gold)" />
              </div>
              <h3 className="font-serif" style={{ fontSize: '1.4rem', color: '#2b2520', marginBottom: '10px' }}>No calls scheduled yet</h3>
              <p style={{ color: '#706152', margin: 0 }}>Use the "Talk to an Expert" button to schedule your first spiritual consultation.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '20px', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))' }}>
              {scheduledCalls.map((call) => (
                 <div key={call.id} className="glass-card" style={{ 
                  background: 'rgba(255, 255, 255, 0.45)', 
                  backdropFilter: 'blur(12px)',
                  borderRadius: '12px', 
                  padding: '25px', 
                  border: '1px solid rgba(181, 137, 37, 0.15)',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.04)',
                  transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                }}
                onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-8px)'; e.currentTarget.style.boxShadow = '0 20px 40px rgba(181, 137, 37, 0.12)'; e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.5)'; }}
                onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.04)'; e.currentTarget.style.borderColor = 'rgba(181, 137, 37, 0.15)'; }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--primary-gold)' }}>
                      <Calendar size={18} />
                      <span style={{ fontWeight: '600', fontSize: '1.1rem', color: '#2b2520' }}>{call.date}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#4a4a4a', background: 'rgba(0,0,0,0.05)', padding: '4px 10px', borderRadius: '20px', fontSize: '0.85rem' }}>
                      <Clock size={14} />
                      <span>{call.time}</span>
                    </div>
                  </div>
                  
                  <div style={{ color: '#2b2520', fontSize: '0.95rem', lineHeight: '1.6', marginBottom: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                      <MessageSquare size={16} style={{ marginTop: '4px', flexShrink: 0, color: 'var(--primary-gold)' }} />
                      <p style={{ margin: 0, fontWeight: '500' }}>"{call.query}"</p>
                    </div>
                  </div>

                  <div style={{ borderTop: '1px solid rgba(0,0,0,0.08)', paddingTop: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                    <span style={{ fontSize: '0.75rem', color: '#706152', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      <span style={{ 
                        fontSize: '0.75rem', 
                        fontWeight: '700', 
                        color: call.status === 'Pending' ? '#f39c12' : 
                               call.status === 'Cancelled' ? '#e74c3c' : '#2ecc71',
                        background: call.status === 'Pending' ? 'rgba(243, 156, 18, 0.1)' : 
                                    call.status === 'Cancelled' ? 'rgba(231, 76, 60, 0.1)' : 'rgba(46, 204, 113, 0.1)',
                        padding: '4px 10px',
                        borderRadius: '20px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}>
                        {call.status || 'Scheduled'}
                      </span>
                      {isTimeReached(call.date, call.time) && call.status !== 'Completed' && call.status !== 'Cancelled' && (
                        <button
                          onClick={() => handleJoinCall(call)}
                          className="btn-gold"
                          style={{
                            padding: '4px 12px',
                            borderRadius: '20px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px'
                          }}
                        >
                          Join Call
                        </button>
                      )}
                      {(call.status === 'Pending' || call.status === 'Confirmed') && (
                        <button
                          onClick={() => handleCancelCall(call.id)}
                          style={{
                            background: 'none',
                            border: '1px solid rgba(231, 76, 60, 0.3)',
                            color: '#e74c3c',
                            padding: '4px 12px',
                            borderRadius: '20px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseOver={(e) => { e.currentTarget.style.background = 'rgba(231, 76, 60, 0.1)'; }}
                          onMouseOut={(e) => { e.currentTarget.style.background = 'none'; }}
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
